import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { format } from "date-fns";
import { Fragment } from "react";

import { MemoizedLabStain } from "shared/selectors/selectLabStains";
import { appendSendoutIfNeeded } from "shared/utils/labUtils";

import "./LabStainSuggestion.css";

const getCutOffTime = (cutOffTime: string) => {
  const [hour, minute, second] = cutOffTime.split(":").map((s) => parseInt(s));
  const currDate = new Date().setUTCHours(hour, minute, second);

  return format(currDate, "h:mm a");
};

export const HighlightedSuggestion = ({
  haystack,
  needle,
}: {
  haystack: string;
  needle: string;
}) => {
  const chunks: { text: string; highlight: boolean }[] = parse(
    haystack,
    match(haystack, needle, { insideWords: true })
  );

  return (
    <>
      {chunks.map((chunk, i) => {
        const { highlight, text } = chunk;
        return highlight ? (
          <span className="suggestion__block__highlighted" key={i}>
            {text}
          </span>
        ) : (
          <Fragment key={i}>{text}</Fragment>
        );
      })}
    </>
  );
};

const LabStainSuggestion = ({
  labstain,
  query,
}: {
  labstain: MemoizedLabStain;
  query?: string;
}) => {
  return (
    <div className="LabStainSuggestion">
      <div className="suggestion__block suggestion__block--header">
        <span>
          <strong>
            <HighlightedSuggestion
              haystack={labstain.stain?.name || ""}
              needle={query || ""}
            />
          </strong>{" "}
          <abbr>
            (
            <code>
              <HighlightedSuggestion
                haystack={labstain.stain?.abbreviation || ""}
                needle={query || ""}
              />
            </code>
            )
          </abbr>
        </span>
        {labstain.external_stain && (
          <span>
            {appendSendoutIfNeeded(labstain.external_stain?.external_lab?.name)}
          </span>
        )}
      </div>
      {labstain.label_override && (
        <div className="suggestion__block suggestion__block--light">
          <small>Prints on label as {labstain.label_override}</small>
        </div>
      )}
      {labstain.routine_stain && (
        <div className="suggestion__block suggestion__block--light">
          <small>
            Routine {labstain.routine_stain.name}{" "}
            <abbr>
              (<code>{labstain.routine_stain.abbreviation}</code>)
            </abbr>
          </small>
        </div>
      )}
      {labstain.non_routine_stain && (
        <div className="suggestion__block suggestion__block--light">
          <small>
            Non-Routine {labstain.non_routine_stain.name}{" "}
            <abbr>
              (<code>{labstain.non_routine_stain.abbreviation}</code>)
            </abbr>
          </small>
        </div>
      )}

      <div className="suggestion__block suggestion__block--light">
        {labstain.cutoff_time && (
          <span>
            <strong>Cutoff:</strong> {getCutOffTime(labstain.cutoff_time)}
          </span>
        )}
        {labstain.turnaround_hours && (
          <span>
            <strong>Turn Around:</strong> {labstain.turnaround_hours} hours
          </span>
        )}
      </div>
    </div>
  );
};

export default LabStainSuggestion;
